import { datadogRum } from '@datadog/browser-rum';

import {
  SettingsGroup,
  SettingsPageFrame,
  usePrivacyModeToggle,
} from '@eluve/blocks';
import {
  Button,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FormHeader,
  FormNewDescription,
  FormSection,
  FormTitle,
  Icon,
  Kbd,
  Separator,
  Stack,
  Switch,
  Text,
} from '@eluve/components';
import {
  EnableSoundEffects,
  useTenantUserSettings,
} from '@eluve/frontend-feature-user-settings';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { MicrophonePermissionProvider } from '@eluve/smart-blocks';
import { sessionSoundPlayer } from '@eluve/sounds';
import { useTenantLocalStorage } from '@eluve/utility-hooks';

import { useShowAppointmentsOwner } from '../../../../settings/hooks/useShowAppointmentsOwner';
import { MicSettings } from '../MicSettings';

const advancedMonitoringThresholdHours = 48;

export const AudioDisplaySettingsPage: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('user_settings');
  const { setPrivacyMode, privacyMode } = usePrivacyModeToggle();
  const [showAppointmentsOwner, setShowAppointmentsOwner] =
    useShowAppointmentsOwner();
  const { settings } = useTenantUserSettings();

  const [enabledAdvancedMonitoring, setEnabledAdvancedMonitoring] =
    useTenantLocalStorage({
      initialValue: false,
      key: 'ADVANCED_MONITORING',
      tenantId,
      ttlInSeconds: 60 * 60 * advancedMonitoringThresholdHours,
    });

  const previewSound = () => {
    // Force-play so the preview also works while the toggle is off.
    sessionSoundPlayer.setIsEnabled(true);
    sessionSoundPlayer.play('start');
    sessionSoundPlayer.setIsEnabled(settings?.enableSoundEffects ?? true);
  };

  return (
    <SettingsPageFrame title="Audio & Display">
      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>Microphone</FormTitle>
          <FormNewDescription>
            Test and pick the audio input used for recordings.
          </FormNewDescription>
        </FormHeader>
        <MicrophonePermissionProvider>
          <MicSettings />
        </MicrophonePermissionProvider>
      </FormSection>

      <SettingsGroup title="Notifications">
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>Sound Effect</FieldLabel>
            <FieldDescription>
              Play a chime when starting or stopping a recording.
            </FieldDescription>
          </FieldContent>
          <EnableSoundEffects />
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldLabel>Preview Sound</FieldLabel>
          <Button variant="outline" size="sm" onClick={previewSound}>
            <Icon name="Play" size="auto" />
            Play
          </Button>
        </Field>
      </SettingsGroup>

      <SettingsGroup title="Privacy & display">
        <Stack gap={3} align="stretch" className="px-6 py-7">
          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel>Privacy Mode</FieldLabel>
              <FieldDescription>
                Hide patient information from your screen. Toggle from anywhere
                with <Kbd>Shift</Kbd> + <Kbd>P</Kbd>.
              </FieldDescription>
            </FieldContent>
            <Switch
              checked={privacyMode}
              onCheckedChange={(checked) => {
                setPrivacyMode(!checked);
                analytics.capture('enable_privacy_mode_update', {
                  tenantId,
                  userId,
                  checked: !checked,
                });
              }}
              size="sm"
              className="data-unchecked:bg-border-tertiary"
              aria-label="Privacy Mode"
            />
          </Field>
          <Text size="xs" color="tertiary" className="italic">
            Example patient name:{' '}
            <span className="-m-2 inline-block p-2 font-semibold privacy-text">
              John Doe
            </span>
          </Text>
        </Stack>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>Show Appointment Owners</FieldLabel>
            <FieldDescription>
              Show who owns each appointment in your agenda.
            </FieldDescription>
          </FieldContent>
          <Switch
            checked={showAppointmentsOwner}
            onCheckedChange={(checked) => {
              setShowAppointmentsOwner(checked);
              analytics.capture('show_owner_appointments_update', {
                tenantId,
                userId,
                checked,
              });
            }}
            size="sm"
            className="data-unchecked:bg-border-tertiary"
            aria-label="Show Appointment Owners"
          />
        </Field>
        <Separator className="my-0" />
        <Field orientation="horizontal" className="px-6 py-7">
          <FieldContent>
            <FieldLabel>Share screen with support</FieldLabel>
            <FieldDescription className="text-balance">
              Temporarily monitor interactions for debugging. This will record
              your interactions on Eluve products for up to{' '}
              {advancedMonitoringThresholdHours} hours and then automatically
              turn off. These recordings will be saved for up to 30 days in a
              HIPAA compliant environment and then be automatically deleted.
            </FieldDescription>
          </FieldContent>
          <Switch
            checked={Boolean(enabledAdvancedMonitoring)}
            onCheckedChange={(checked) => {
              setEnabledAdvancedMonitoring(checked);
              datadogRum.setTrackingConsent(
                checked ? 'granted' : 'not-granted',
              );
              analytics.capture('enable_advanced_monitoring_update', {
                tenantId,
                userId,
                checked,
              });
            }}
            size="sm"
            className="data-unchecked:bg-border-tertiary"
            aria-label="Share screen with support"
          />
        </Field>
      </SettingsGroup>
    </SettingsPageFrame>
  );
};
